import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '@/shared/api/errors';

import { type ClientListParams, clientKeys, clientsApi } from './api';
import type { Client, ClientCreate, ClientLookupRead, ClientUpdate } from './types';

export function useClientList(params: ClientListParams) {
  return useQuery({
    queryKey: clientKeys.list(params),
    queryFn: () => clientsApi.list(params),
  });
}

export function useClient(id: string | undefined | null) {
  return useQuery<Client, ApiError>({
    queryKey: clientKeys.detail(id ?? ''),
    queryFn: () => clientsApi.detail(id ?? ''),
    enabled: Boolean(id),
  });
}

/**
 * Writing a client: the answer goes straight into the cache and only the lists are
 * invalidated.
 *
 * Never `clientKeys.all`, which is a prefix of the detail key: invalidating it would fire
 * a refetch of the client just written and put the previous version back on screen for as
 * long as the round trip takes.
 */
function useClientMutation<TVariables>(mutationFn: (variables: TVariables) => Promise<Client>) {
  const queryClient = useQueryClient();
  return useMutation<Client, ApiError, TVariables>({
    mutationFn,
    onSuccess: (client) => {
      queryClient.setQueryData(clientKeys.detail(client.id), client);
      void queryClient.invalidateQueries({ queryKey: clientKeys.lists });
    },
  });
}

export function useCreateClient() {
  return useClientMutation<ClientCreate>((payload) => clientsApi.create(payload));
}

export function useUpdateClient(id: string) {
  return useClientMutation<ClientUpdate>((payload) => clientsApi.update(id, payload));
}

/**
 * «Buscar en Profer» (contract v10.46). What Profer answers is already a row of Clientes
 * when this resolves, so the lists are invalidated: the next search finds it by name too.
 */
export function useClientLookup() {
  const queryClient = useQueryClient();
  return useMutation<ClientLookupRead, ApiError, string>({
    mutationFn: (query) => clientsApi.lookup(query),
    onSuccess: (answer) => {
      for (const client of answer.items ?? []) {
        queryClient.setQueryData(clientKeys.detail(client.id), client);
      }
      if ((answer.items ?? []).length > 0) {
        void queryClient.invalidateQueries({ queryKey: clientKeys.lists });
      }
    },
  });
}
